/**
 * Locale-aware file-size formatting. Generic (takes a number + a locale, knows no domain type), so
 * it belongs in core alongside the date formatters.
 */

/**
 * Binary units, which is what the backend's limits are expressed in — `max_size_bytes: 10485760` is
 * 10 MiB, not 10 MB. Labelled with the familiar KB/MB/GB rather than KiB/MiB/GiB: applicants read
 * "10 MB", and the design nowhere uses the binary spellings.
 */
const UNITS = ["B", "KB", "MB", "GB"] as const;

/**
 * Format a byte count for display: `10485760` → `"10 MB"`.
 *
 * Goes through `Intl.NumberFormat` so the decimal separator follows the language — a hand-built `toFixed(1)` would print
 * "10.5" to a French reader who writes "10,5".
 *
 * At most one decimal, and never a trailing `.0`, so whole sizes read "10 MB" rather than "10.0 MB".
 */
export function formatBytes(bytes: number, locale: string): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "";

  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }

  const formatted = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
  }).format(value);

  return `${formatted} ${UNITS[unit]}`;
}
